import { Injectable, signal, WritableSignal, inject } from '@angular/core';
import { buildQueryFilter } from '@shared/helpers/build-query-filter';
import { removeDuplicatedItemsById } from '@shared/helpers/functions/remove-duplicated-items-by-id';
import { HeaderService } from '@shared/services';
import { PlainObject, QueryOptions } from 'odata-query';
import { map, Observable, switchMap } from 'rxjs';
import {
  CountryTaxNewsResponse,
  CountryTaxNewsWithNestedObjectsResponse,
  CountryTaxNewsWithNestedObjectsResponseODataWithMetadataResponse,
  CreateCountryTaxNewsRequest,
  GstarClient,
  UpdateCountryTaxNewsRequest,
} from 'src/app/web-api-client';
import { Collection } from '../../admin/models';
import { mapTaxNewsFromResponse } from '../mappers/map-tax-news-from-response';
import { TaxNews } from '../models/tax-news';

@Injectable()
export class TaxNewsService {
  private readonly gstarClient = inject(GstarClient);
  private readonly headerService = inject(HeaderService);

  query: WritableSignal<Partial<QueryOptions<TaxNews>>> = signal(
    {
      count: true,
      orderBy: 'newsDate desc',
    },
    {
      equal: (
        _a: Partial<QueryOptions<TaxNews>>,
        _b: Partial<QueryOptions<TaxNews>>
      ) => false,
    }
  );

  lastNewsPage: WritableSignal<number | null> = signal(null);

  getTaxNewsByCountry(countryId: number): Observable<Collection<TaxNews>> {
    this.updateQuery({
      filter: {
        countryTaxNewsLinks: {
          any: {
            country: {
              countryId: parseInt(countryId.toString()),
            },
          },
        },
      },
    });
    const query = this.query();

    return this.gstarClient
      .byclient(
        this.headerService.selectedClient()?.id,
        query?.select?.toString(),
        buildQueryFilter(query),
        query?.top?.toString(),
        query?.skip?.toString(),
        query?.orderBy?.toString(),
        query?.count?.toString(),
        undefined
      )
      .pipe(
        map(
          (
            response: CountryTaxNewsWithNestedObjectsResponseODataWithMetadataResponse
          ) => ({
            total: response.odataCount ?? 0,
            items:
              removeDuplicatedItemsById<CountryTaxNewsWithNestedObjectsResponse>(
                response.value ?? [],
                'countryTaxNewsId'
              ).map((item: CountryTaxNewsWithNestedObjectsResponse) =>
                mapTaxNewsFromResponse(item, countryId)
              ) ?? [],
          })
        )
      );
  }

  updateQuery(query: PlainObject): void {
    const currentFilter = {
      ...(this.query().filter as PlainObject),
      ...query['filter'],
    };

    const currentQuery = {
      ...this.query(),
      ...query,
    };
    currentQuery.filter = currentFilter;

    if (query['filter'] === undefined) {
      delete currentQuery.filter;
    }

    this.query.set(currentQuery);
  }

  addTaxNews(
    request: CreateCountryTaxNewsRequest,
    countryId: number
  ): Observable<TaxNews | undefined> {
    return this.gstarClient.countrytaxnewsPOST(request).pipe(
      switchMap(({ countryTaxNewsId }: CountryTaxNewsResponse) => {
        return this.getTaxNewsById(countryTaxNewsId!, countryId);
      })
    );
  }

  updateTaxNews(
    countryInformationId: number,
    request: UpdateCountryTaxNewsRequest,
    countryId: number
  ): Observable<TaxNews | undefined> {
    return this.gstarClient
      .countrytaxnewsPUT(countryInformationId, request)
      .pipe(
        switchMap(() => {
          return this.getTaxNewsById(request.countryTaxNewsId, countryId);
        })
      );
  }

  getTaxNewsById(
    taxNewsId: number,
    countryId: number
  ): Observable<TaxNews | undefined> {
    const byIdQuery = {
      filter: { countryTaxNewsId: parseInt(taxNewsId.toString()) },
    };
    return this.gstarClient
      .byclient(
        this.headerService.selectedClient()?.id,
        undefined,
        buildQueryFilter(byIdQuery),
        undefined,
        undefined,
        undefined,
        undefined,
        undefined
      )
      .pipe(
        map(
          (
            items: CountryTaxNewsWithNestedObjectsResponseODataWithMetadataResponse
          ) => {
            if (items.value) {
              return mapTaxNewsFromResponse(items.value[0], countryId);
            }
            return undefined;
          }
        )
      );
  }

  deleteTaxNews(taxNewsId: number): Observable<void> {
    return this.gstarClient.delete2([taxNewsId]).pipe();
  }
}
